<template>
  <div class="launch-analysis-dialog-root">
    <v-dialog :value="value" max-width="760" persistent scrollable @input="onDialogInput">
      <v-card class="launch-analysis-dialog">
        <v-card-title class="px-6">
          <h5 class="primary-color mb-0">{{ title }}</h5>
          <v-spacer />
          <v-btn icon aria-label="Close" :disabled="launching" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle v-if="targets.length" class="px-6 pt-1 launch-analysis-dialog__targets">
          {{ targetsLabel }}
        </v-card-subtitle>

        <v-card-text class="px-2 py-0">
          <v-stepper
            v-model="step"
            vertical
            flat
            class="launch-analysis-dialog__stepper elevation-0"
          >
            <template v-for="(item, index) in steps">
              <v-stepper-step
                :key="`step-${item.key}`"
                :step="index + 1"
                :complete="step > index + 1"
                :editable="!launching && step > index + 1"
                :rules="[() => !stepHasError(item.key)]"
              >
                {{ item.label }}
                <small v-if="stepSummary(item.key)" class="mt-1">{{ stepSummary(item.key) }}</small>
              </v-stepper-step>

              <v-stepper-content :key="`content-${item.key}`" :step="index + 1">
                <!-- 0. Groups (mode 'groups', analysisGroups flag) -->
                <div v-if="item.key === 'groups'" class="launch-analysis-dialog__step">
                  <v-select
                    v-model="selectedGroupIds"
                    label="Analysis groups"
                    :items="groupItems"
                    item-text="text"
                    item-value="value"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    dense
                    outlined
                    hide-details="auto"
                    :loading="loadingGroups"
                    :disabled="launching"
                    hint="One run per video and group, with each group's configuration and engines. Leave empty to configure the run below."
                    persistent-hint
                    class="launch-analysis-dialog__groups"
                    attach
                  />
                  <v-checkbox
                    v-if="selectedGroupIds.length"
                    v-model="addMissingMembers"
                    dense
                    hide-details
                    class="mt-3 launch-analysis-dialog__add-missing"
                    label="Add the videos to the groups when they are not members yet"
                    :disabled="launching"
                  />
                  <p
                    v-if="selectedGroupIds.length"
                    class="caption grey--text text--darken-1 mt-2 mb-0"
                  >
                    Group runs never promote and never change the active run of a video.
                  </p>
                </div>

                <!-- 1. Configuration -->
                <div v-else-if="item.key === 'config'" class="launch-analysis-dialog__step">
                  <v-select
                    v-model="analysisTypeId"
                    label="Analysis configuration *"
                    :items="analysisTypes"
                    :item-text="configLabel"
                    item-value="id"
                    :loading="loadingAnalysisTypes"
                    :disabled="loadingAnalysisTypes"
                    dense
                    outlined
                    hide-details="auto"
                    class="launch-analysis-dialog__config"
                    :rules="[(v) => !!v || 'Select a configuration']"
                    attach
                  />
                </div>

                <!-- 2. Version (runHistory flag) -->
                <div v-else-if="item.key === 'version'" class="launch-analysis-dialog__step">
                  <v-progress-linear v-if="loadingVersions" indeterminate color="primary" />
                  <v-radio-group v-model="configVersionId" dense hide-details class="mt-0">
                    <v-radio :value="null" label="Latest version" />
                    <v-radio
                      v-for="version in versions"
                      :key="version.id"
                      :value="version.id"
                      :label="versionLabel(version)"
                    />
                  </v-radio-group>
                  <p
                    v-if="!loadingVersions && versions.length === 0"
                    class="caption grey--text mb-0"
                  >
                    No stored versions yet; the latest configuration will be used.
                  </p>
                </div>

                <!-- 3. Engines -->
                <div v-else-if="item.key === 'engines'" class="launch-analysis-dialog__step">
                  <EngineSelector
                    v-model="engines"
                    :source-type="sourceType"
                    :disabled="launching"
                    @change="onEnginesChange"
                    @catalogue-loaded="onCatalogueLoaded"
                  />
                  <p v-if="selection.auto_added.length" class="caption primary--text mt-2 mb-0">
                    Added automatically: {{ selection.auto_added.map(labelOf).join(', ') }}
                  </p>
                </div>

                <!-- 4. Batch and note -->
                <div v-else-if="item.key === 'batch'" class="launch-analysis-dialog__step">
                  <v-combobox
                    v-model="batch"
                    label="Batch"
                    :items="batchSuggestions"
                    hint="Groups the launched runs under one name (recent batches suggested)"
                    persistent-hint
                    dense
                    outlined
                    clearable
                    attach
                    class="mb-3"
                  />
                  <v-textarea
                    v-model="note"
                    label="Note"
                    rows="2"
                    auto-grow
                    dense
                    outlined
                    counter="500"
                    maxlength="500"
                    hide-details="auto"
                  />
                </div>

                <!-- 5. Promote on success -->
                <div v-else-if="item.key === 'promote'" class="launch-analysis-dialog__step">
                  <v-switch
                    v-model="promoteOnSuccess"
                    inset
                    dense
                    color="primary"
                    hide-details
                    class="mt-0"
                    label="Promote on success"
                  />
                  <p class="caption grey--text text--darken-1 mt-1 mb-0">
                    When on, the new run becomes the active result of each
                    {{ targetNoun }} as soon as it succeeds. Previous runs stay available in the run
                    history.
                  </p>
                </div>

                <div class="d-flex align-center mt-4">
                  <v-btn v-if="index > 0" text small :disabled="launching" @click="step = index">
                    Back
                  </v-btn>
                  <v-spacer />
                  <v-btn
                    v-if="index < steps.length - 1"
                    small
                    color="primary"
                    depressed
                    :disabled="!canContinueFrom(item.key)"
                    @click="step = index + 2"
                  >
                    Continue
                  </v-btn>
                </div>
              </v-stepper-content>
            </template>
          </v-stepper>

          <v-alert v-if="launchError" dense text type="error" class="mx-4 mt-2 mb-0">
            {{ launchError }}
          </v-alert>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-6 py-3 launch-analysis-dialog__footer">
          <span class="caption grey--text text--darken-2 launch-analysis-dialog__summary">
            {{ summaryLine }}
          </span>
          <v-spacer />
          <v-btn text class="cancel-modal" :disabled="launching" @click="close">Cancel</v-btn>
          <v-btn
            class="ok-modal launch-analysis-dialog__launch"
            :disabled="!canLaunch"
            :loading="launching"
            @click="launch"
          >
            Launch analysis
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.visible" :timeout="6000" :color="snackbar.color" bottom>
      {{ snackbar.text }}
      <template #action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.visible = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import EngineSelector from '@/components/analysis/EngineSelector.vue';
import featureFlags from '@/config/featureFlags';
import AnalysisRunsService from '@/services/AnalysisRunsService';
import InterviewsDataService from '@/services/InterviewsDataService';
import ManualAnalysisService from '@/services/ManualAnalysisService';
import VersionService from '@/services/VersionService';
import VideosDataService from '@/services/VideosDataService';
import { activeConfigId, analysisConfigLabel } from '@/utils/activeAnalysisConfig';
import {
  buildRunOptions,
  defaultSelection,
  engineLabel,
  formatLaunchSummary,
} from '@/utils/engineRules';

/** Groups client, loaded only in mode 'groups' (keeps the default dialog free of the module). */
async function groupsService() {
  const module = await import('@/services/AnalysisGroupsService');
  return module.default;
}

const EMPTY_SELECTION = Object.freeze({
  engines: [],
  requested: [],
  auto_added: [],
  warnings: [],
  valid: false,
  error: 'engines_empty',
});

/**
 * Launch analysis dialog (M3 contract 9): configuration -> version (runHistory flag) ->
 * engines -> batch and note -> promote on success, with a summary line and a single Launch
 * button. It performs the launch itself:
 *   - interview targets: `POST videos/reanalyze/:id` per target;
 *   - one manual target: `POST manual-analysis/:id/reanalyze`;
 *   - several manual targets: `POST manual-analysis/bulk-reanalyze` (shared batch);
 *   - mode 'groups' with groups selected (M4 contract 8): `POST analysis-groups/run-selection`
 *     (one run per target and group; configuration and engines come from each group).
 * Emits `launched` `{ accepted: [{ id, run_id, position }], rejected: [{ id, reason }], batch,
 * engines, auto_added, analysisTypeId }` and shows the "Queued N analyses" snackbar.
 */
export default {
  name: 'LaunchAnalysisDialog',
  components: { EngineSelector },
  props: {
    value: { type: Boolean, default: false },
    /** 'interview' | 'manual_analysis' */
    sourceType: { type: String, required: true },
    /** [{ id, name, analysisTypeId? }] */
    targets: { type: Array, default: () => [] },
    /** [{ id, name }] analysis configurations selectable in step 1 */
    analysisTypes: { type: Array, default: () => [] },
    loadingAnalysisTypes: { type: Boolean, default: false },
    socketId: { type: String, default: '' },
    /**
     * Engines preselected when the dialog opens (e.g. 'Launch with this engine' from an
     * EngineEmptyState). Empty/null -> every selectable engine of the source.
     */
    initialEngines: { type: Array, default: null },
    /**
     * 'groups' adds a first step to pick analysis groups (analysisGroups flag); with at least
     * one group selected the run goes through `run-selection` and the config / version /
     * engines / promote steps are hidden.
     */
    mode: { type: String, default: 'default' },
    /** Groups selectable in the Groups step; loaded from the API when null. */
    groups: { type: Array, default: null },
    initialGroupIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      step: 1,
      analysisTypeId: null,
      versions: [],
      loadingVersions: false,
      configVersionId: null,
      engines: [],
      selection: EMPTY_SELECTION,
      catalogue: [],
      batch: '',
      note: '',
      promoteOnSuccess: true,
      batchSuggestions: [],
      engineVersions: null,
      launching: false,
      launchError: '',
      snackbar: { visible: false, text: '', color: 'success' },
      selectedGroupIds: [],
      addMissingMembers: true,
      loadedGroups: [],
      loadingGroups: false,
    };
  },
  computed: {
    runHistoryEnabled() {
      return Boolean(featureFlags.runHistory);
    },
    groupsMode() {
      return this.mode === 'groups' && Boolean(featureFlags.analysisGroups);
    },
    availableGroups() {
      return Array.isArray(this.groups) ? this.groups : this.loadedGroups;
    },
    groupItems() {
      return this.availableGroups.map((group) => ({
        value: Number(group.id),
        text: `${group.name}${group.members != null ? ` (${group.members} videos)` : ''}`,
      }));
    },
    selectedGroups() {
      return this.availableGroups.filter((group) =>
        this.selectedGroupIds.includes(Number(group.id))
      );
    },
    /** True when the launch goes through the groups (run-selection) instead of a configuration. */
    usingGroups() {
      return this.groupsMode && this.selectedGroupIds.length > 0;
    },
    steps() {
      const steps = [];
      if (this.groupsMode) steps.push({ key: 'groups', label: 'Groups' });
      if (this.usingGroups) {
        steps.push({ key: 'batch', label: 'Batch and note' });
        return steps;
      }
      steps.push({ key: 'config', label: 'Configuration' });
      if (this.runHistoryEnabled) steps.push({ key: 'version', label: 'Version' });
      steps.push({ key: 'engines', label: 'Engines' });
      steps.push({ key: 'batch', label: 'Batch and note' });
      steps.push({ key: 'promote', label: 'Promote on success' });
      return steps;
    },
    targetNoun() {
      return this.sourceType === 'interview' ? 'interview' : 'video';
    },
    title() {
      return this.targets.length > 1
        ? `Launch analysis · ${this.targets.length} ${this.targetNoun}s`
        : 'Launch analysis';
    },
    targetsLabel() {
      const names = this.targets.map((target) => target.name || `#${target.id}`);
      if (names.length <= 3) return names.join(', ');
      return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
    },
    selectedType() {
      return (
        this.analysisTypes.find((type) => Number(type.id) === Number(this.analysisTypeId)) || null
      );
    },
    selectedVersion() {
      if (!this.configVersionId) return this.versions[0] || null;
      return this.versions.find((version) => version.id === this.configVersionId) || null;
    },
    runOptions() {
      return buildRunOptions({
        engines: this.selection.requested,
        configVersionId: this.configVersionId,
        batch: typeof this.batch === 'string' ? this.batch : '',
        note: this.note,
        promoteOnSuccess: this.promoteOnSuccess,
      });
    },
    summaryLine() {
      if (this.usingGroups) {
        const runs = this.targets.length * this.selectedGroupIds.length;
        const names = this.selectedGroups.map((group) => group.name).join(', ');
        return `Launch ${runs} ${runs === 1 ? 'run' : 'runs'} (${this.targets.length} ${
          this.targetNoun
        }${this.targets.length === 1 ? '' : 's'} × ${this.selectedGroupIds.length} ${
          this.selectedGroupIds.length === 1 ? 'group' : 'groups'
        }) · ${names}`;
      }
      return formatLaunchSummary({
        engines: this.selection.engines,
        versions: this.engineVersions,
        configName: this.selectedType?.name || null,
        configVersion: this.selectedVersion?.version ?? null,
        targetCount: this.targets.length,
        targetNoun: this.targetNoun,
      });
    },
    canLaunch() {
      if (this.usingGroups) return !this.launching && this.targets.length > 0;
      return (
        !this.launching &&
        this.targets.length > 0 &&
        Boolean(this.analysisTypeId) &&
        this.selection.valid
      );
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
    usingGroups() {
      // The step list changes shape; keep the user on the Groups step.
      if (this.step > this.steps.length) this.step = 1;
    },
    analysisTypeId(id) {
      this.configVersionId = null;
      if (this.runHistoryEnabled && id) this.loadVersions(id);
    },
    /** Configurations may arrive after the dialog opened (lazy load): pick the default then. */
    analysisTypes(types) {
      if (this.analysisTypeId || !Array.isArray(types) || !types.length) return;
      this.analysisTypeId = this.defaultAnalysisTypeId(types);
    },
  },
  created() {
    if (this.value) this.reset();
  },
  methods: {
    labelOf(name) {
      return engineLabel(this.catalogue, name);
    },
    configLabel(config) {
      return analysisConfigLabel(config);
    },
    /**
     * A target's own configuration first, so a re-run repeats what it ran with; else the active
     * one, which Beon analyses with; the first only on a platform where none is active.
     */
    defaultAnalysisTypeId(types) {
      const fromTarget = this.targets.find((target) => target.analysisTypeId)?.analysisTypeId;
      return fromTarget ?? activeConfigId(types) ?? types[0]?.id ?? null;
    },
    reset() {
      this.step = 1;
      this.launching = false;
      this.launchError = '';
      this.engines = this.initialSelection();
      this.selection = EMPTY_SELECTION;
      this.batch = '';
      this.note = '';
      this.promoteOnSuccess = true;
      this.versions = [];
      this.configVersionId = null;
      const initial = this.defaultAnalysisTypeId(this.analysisTypes);
      if (initial === this.analysisTypeId) {
        if (this.runHistoryEnabled && initial) this.loadVersions(initial);
      } else {
        this.analysisTypeId = initial;
      }
      this.selectedGroupIds = (this.initialGroupIds || []).map((id) => Number(id));
      this.addMissingMembers = true;
      this.loadBatchSuggestions();
      this.loadEngineVersions();
      if (this.groupsMode && !Array.isArray(this.groups)) this.loadGroups();
    },
    async loadGroups() {
      this.loadingGroups = true;
      try {
        const service = await groupsService();
        const response = await service.list({ limit: 200 });
        const body = response?.data?.data ?? response?.data ?? {};
        this.loadedGroups = Array.isArray(body.items)
          ? body.items
          : Array.isArray(body)
            ? body
            : [];
      } catch (error) {
        this.loadedGroups = [];
      } finally {
        this.loadingGroups = false;
      }
    },
    /**
     * Engines to start from: the `initialEngines` preset, else every selectable engine once the
     * catalogue is known (EngineSelector only applies its own default on first load; a reopened
     * dialog would otherwise start with no engine selected).
     */
    initialSelection() {
      const preset = Array.isArray(this.initialEngines)
        ? this.initialEngines.filter((name) => typeof name === 'string' && name)
        : [];
      if (preset.length) return Array.from(new Set(preset));
      return this.catalogue.length ? defaultSelection(this.catalogue, this.sourceType) : [];
    },
    async loadVersions(id) {
      this.loadingVersions = true;
      try {
        const response = await InterviewsDataService.getConfigVersions(id);
        const items = response?.data?.data;
        this.versions = Array.isArray(items) ? items : [];
      } catch (error) {
        this.versions = [];
      } finally {
        this.loadingVersions = false;
      }
    },
    async loadBatchSuggestions() {
      this.batchSuggestions = await AnalysisRunsService.recentBatches({
        source_type: this.sourceType,
        limit: 100,
      });
    },
    async loadEngineVersions() {
      if (this.engineVersions) return;
      try {
        const response = await VersionService.get();
        const engines = response?.data?.ai_service?.engines;
        this.engineVersions = engines && typeof engines === 'object' ? engines : null;
      } catch (error) {
        this.engineVersions = null;
      }
    },
    versionLabel(version) {
      const parts = [`v${version.version}`];
      if (version.created_at) parts.push(new Date(version.created_at).toLocaleDateString());
      if (version.notes) parts.push(version.notes);
      if (version.used_by_runs) parts.push(`used by ${version.used_by_runs} runs`);
      return parts.join(' · ');
    },
    onEnginesChange(result) {
      this.selection = result || EMPTY_SELECTION;
    },
    onCatalogueLoaded(catalogue) {
      this.catalogue = Array.isArray(catalogue) ? catalogue : [];
    },
    stepHasError(key) {
      if (key === 'config') return !this.analysisTypeId && this.step > 1;
      if (key === 'engines')
        return !this.selection.valid && this.step > this.stepIndex('engines') + 1;
      return false;
    },
    stepIndex(key) {
      return this.steps.findIndex((item) => item.key === key);
    },
    stepSummary(key) {
      if (key === 'groups') {
        return this.selectedGroups.length
          ? this.selectedGroups.map((group) => group.name).join(', ')
          : 'No group · configure the run below';
      }
      if (key === 'config') return this.selectedType?.name || '';
      if (key === 'version') {
        return this.configVersionId
          ? `v${this.selectedVersion?.version ?? '?'}`
          : this.versions.length
            ? `Latest (v${this.versions[0].version})`
            : 'Latest';
      }
      if (key === 'engines') return this.selection.engines.map(this.labelOf).join(', ');
      if (key === 'batch') {
        const bits = [];
        if (typeof this.batch === 'string' && this.batch.trim()) bits.push(this.batch.trim());
        if (this.note.trim()) bits.push('note');
        return bits.join(' · ');
      }
      if (key === 'promote') return this.promoteOnSuccess ? 'On' : 'Off';
      return '';
    },
    canContinueFrom(key) {
      if (this.launching) return false;
      if (key === 'config') return Boolean(this.analysisTypeId);
      if (key === 'engines') return this.selection.valid;
      return true;
    },
    onDialogInput(open) {
      if (!open) this.close();
    },
    close() {
      if (this.launching) return;
      this.$emit('input', false);
    },
    async launch() {
      if (!this.canLaunch) return;
      this.launching = true;
      this.launchError = '';
      const runOptions = this.runOptions;
      const accepted = [];
      const rejected = [];
      let batch = runOptions.batch || null;

      try {
        if (this.usingGroups) {
          const service = await groupsService();
          const response = await service.runSelection({
            group_ids: this.selectedGroupIds,
            items: this.targets.map((target) => ({
              source_type: this.sourceType,
              source_id: target.id,
            })),
            add_missing_members: this.addMissingMembers,
            batch: runOptions.batch || null,
            note: runOptions.note || null,
          });
          const body = response?.data?.data ?? response?.data ?? {};
          (body.accepted || []).forEach((item) =>
            accepted.push({
              id: item.source_id,
              run_id: item.run_id ?? null,
              position: item.position ?? null,
              group_id: item.group_id ?? null,
            })
          );
          (body.skipped || []).forEach((item) =>
            rejected.push({
              id: item.source_id,
              reason: item.reason,
              group_id: item.group_id ?? null,
            })
          );
          if (body.batch) batch = body.batch;
        } else if (this.sourceType === 'manual_analysis' && this.targets.length > 1) {
          const response = await ManualAnalysisService.bulkReanalyze(
            this.targets.map((target) => target.id),
            runOptions,
            { analysisTypeId: this.analysisTypeId, socketId: this.socketId }
          );
          const body = response?.data?.data ?? response?.data ?? {};
          (body.accepted || []).forEach((item) => accepted.push(item));
          (body.rejected || []).forEach((item) => rejected.push(item));
          if (body.batch) batch = body.batch;
        } else {
          for (const target of this.targets) {
            try {
              const response =
                this.sourceType === 'interview'
                  ? await VideosDataService.reanalyze(target.id, {
                      analysisTypeId: this.analysisTypeId,
                      socketId: this.socketId,
                      runOptions,
                    })
                  : await ManualAnalysisService.reanalyze(target.id, {
                      analysisTypeId: this.analysisTypeId,
                      socketId: this.socketId,
                      runOptions,
                    });
              const body = response?.data?.data ?? {};
              accepted.push({
                id: target.id,
                run_id: body.run_id ?? body.runId ?? null,
                position: body.position ?? null,
              });
            } catch (error) {
              rejected.push({ id: target.id, reason: this.errorReason(error) });
            }
          }
        }
      } catch (error) {
        this.launching = false;
        this.launchError = this.errorReason(error);
        this.$emit('error', error);
        return;
      }

      this.launching = false;
      const result = {
        accepted,
        rejected,
        batch,
        engines: this.selection.engines,
        auto_added: this.selection.auto_added,
        analysisTypeId: this.usingGroups ? null : this.analysisTypeId,
        runOptions,
        groupIds: this.usingGroups ? [...this.selectedGroupIds] : [],
      };

      if (accepted.length === 0) {
        this.launchError = rejected[0]?.reason || 'Could not queue the analysis.';
        this.$emit('error', result);
        return;
      }

      this.$emit('launched', result);
      this.$emit('input', false);
      const plural = accepted.length === 1 ? 'analysis' : 'analyses';
      const parts = [`Queued ${accepted.length} ${plural}`];
      if (batch) parts.push(`batch ${batch}`);
      if (rejected.length) parts.push(`${rejected.length} rejected`);
      this.snackbar = {
        visible: true,
        text: parts.join(' · '),
        color: rejected.length ? 'warning' : 'success',
      };
    },
    errorReason(error) {
      const data = error?.response?.data;
      if (typeof data?.message === 'string' && data.message) return data.message;
      if (typeof data?.error === 'string' && data.error) return data.error;
      if (typeof error?.reason === 'string') return error.reason;
      return error?.message || 'Request failed';
    },
  },
};
</script>

<style scoped>
.launch-analysis-dialog__stepper {
  background: transparent;
}
.launch-analysis-dialog__step {
  padding-top: 4px;
}
.launch-analysis-dialog__footer {
  flex-wrap: wrap;
  gap: 8px;
}
.launch-analysis-dialog__summary {
  flex: 1 1 260px;
  min-width: 0;
  overflow-wrap: anywhere;
}
.launch-analysis-dialog__targets {
  overflow-wrap: anywhere;
}
</style>
